import cn from 'classnames/bind'
import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import {
  PlainInputMaskedCustom,
  PlainInputMaskedCustomProps,
  maskType,
} from '../plain-input-masked'

import PlainDropdownSurface, { SurfaceType } from '../plain-dropdown-surface'
import PlainIcon, { iconTypes, colorTypes } from '../plain-icon'
import { SelectorGroup } from './selector-group'
import { getInputTimeParts } from './get-input-time-parts'
import { dispatchFormChanged } from '../custom-events'

import styles from './plain-input-time.module.scss'

const cx = cn.bind(styles)

export type PlainInputTimeProps = {
  id: string
  value: string
  disabled?: boolean
  weekdays?: boolean
  weekdaysShort?: boolean
  displayAsBlock?: boolean
} & Omit<PlainInputMaskedCustomProps, 'predefinedMaskType'>

export const PlainInputTime = ({
  id,
  value,
  disabled,
  weekdays = false,
  weekdaysShort = false,
  displayAsBlock,
  className,
  onValue,
  ...others
}: PlainInputTimeProps) => {
  const [state, setState] = useState<string | null>(null)
  const actualValue = state === null ? value : state

  const { weekday, hours, minutes, valid } = useMemo(
    () => getInputTimeParts(actualValue, weekdays || weekdaysShort),
    [actualValue, weekdays, weekdaysShort],
  )

  useEffect(() => {
    if (valid && state) {
      onValue?.(state)
    }
  }, [state, valid]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setState(null)
  }, [value])

  const handleSelectorChange = (hours: number | null, minutes: number | null, weekday: number) => {
    if (
      (weekdays || weekdaysShort) &&
      (hours === null || minutes === null || weekday < 0 || weekday > 6)
    )
      return
    if (hours === null || minutes === null) return
    const time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    const newValue = weekdays || weekdaysShort ? `${moment.weekdays(true, weekday)} ${time}` : time
    setState(null)
    onValue?.(newValue)
    dispatchFormChanged()
  }

  const renderSelector = (_: () => void) => (
    <SelectorGroup
      hours={hours}
      minutes={minutes}
      weekday={weekday}
      showWeeks={weekdays || weekdaysShort}
      onChange={handleSelectorChange}
    />
  )

  return (
    <div className={cx(styles.root, { displayAsBlock }, className)}>
      <PlainInputMaskedCustom
        id={id}
        name={id}
        value={actualValue}
        disabled={disabled}
        onValue={setState}
        onBlur={() => setState(null)}
        {...others}
        className={cx(styles.time, { weekdays, weekdaysShort })}
        predefinedMaskType={(() => {
          if (weekdays) {
            return maskType.MASK_WEEKDAY_TIME
          }
          if (weekdaysShort) {
            return maskType.MASK_WEEKDAY_SHORT_TIME
          }
          return maskType.MASK_TIME_CUSTOM
        })()}
      />
      <div className={styles.selector}>
        <PlainDropdownSurface id="date" type={SurfaceType.Grid} surfaceContent={renderSelector}>
          {(onClick, _, areaProps) => (
            <button
              type="button"
              className={cx(styles.button, { disabled })}
              onClick={onClick}
              disabled={disabled}
              {...areaProps}
            >
              <PlainIcon
                type={iconTypes.CALENDAR}
                colorType={disabled ? colorTypes.COLOR_GRAY : colorTypes.COLOR_PRIMARY}
                width={18}
                height="auto"
              />
            </button>
          )}
        </PlainDropdownSurface>
      </div>
    </div>
  )
}
